// Location: frontend/src/admin/pages/ManagePortfolio.jsx
// Refactored to Tailwind CSS with Dynamic Backend Integration

import React, { useEffect, useState } from 'react';
import * as MdIcons from 'react-icons/md';
import * as FaIcons from 'react-icons/fa';
import * as AiIcons from 'react-icons/ai';
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:4100";

const ManagePortfolio = () => {
    const [portfolios, setPortfolios] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [viewData, setViewData] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [imagePreview, setImagePreview] = useState(null);

    const [formData, setFormData] = useState({
        name: "",
        category: "",
        businessType: "",
        platform: "",
        url: "",
        videoLink: "",
        description: "",
        image: null,
        show: true
    });

    // ============== Helper to helper URL ==============
    // Ensures URL has http/https protocol
    const formatUrl = (url) => {
        if (!url) return "#";
        if (url.startsWith("http://") || url.startsWith("https://")) {
            return url;
        }
        return `https://${url}`;
    };

    // ============== Fetch Portfolios ==============
    useEffect(() => {
        fetchPortfolios();
    }, []);

    const fetchPortfolios = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/admin/portfolio/get`, {
                withCredentials: true
            });
            if (res.data.success) {
                setPortfolios(res.data.data);
            }
        } catch (error) {
            console.error("Error fetching portfolios:", error);
            // alert("Failed to fetch portfolios");
        } finally {
            setLoading(false);
        }
    };

    // ============== Handle Form Input ==============
    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setFormData({ ...formData, image: file });
            setImagePreview(URL.createObjectURL(file));
        }
    };

    // ============== Reset Form ==============
    const resetForm = () => {
        setFormData({
            name: "",
            category: "",
            businessType: "",
            platform: "",
            url: "",
            videoLink: "",
            description: "",
            image: null,
            show: true
        });
        setEditingId(null);
        setImagePreview(null);
        setShowModal(false);
    };

    // ============== Submit Form ==============
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        const data = new FormData();
        data.append('name', formData.name);
        data.append('category', formData.category);
        data.append('businessType', formData.businessType);
        data.append('platform', formData.platform);
        data.append('url', formData.url);
        data.append('videoLink', formData.videoLink);
        data.append('description', formData.description);
        data.append('show', formData.show);
        if (formData.image) {
            data.append('image', formData.image);
        }

        try {
            if (editingId) {
                const res = await axios.put(`${API_URL}/admin/portfolio/update/${editingId}`, data, {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true
                });
                if (res.data.success) {
                    alert("Portfolio updated successfully");
                    fetchPortfolios();
                    resetForm();
                }
            } else {
                const res = await axios.post(`${API_URL}/admin/portfolio/add`, data, {
                    headers: { "Content-Type": "multipart/form-data" },
                    withCredentials: true
                });
                if (res.data.success) {
                    alert("Portfolio added successfully");
                    fetchPortfolios();
                    resetForm();
                }
            }
        } catch (error) {
            console.error("Error saving portfolio:", error);
            alert("Failed to save portfolio");
        } finally {
            setLoading(false);
        }
    };

    // ============== Edit Setup ==============
    const handleEdit = (item) => {
        setFormData({
            name: item.name,
            category: item.category,
            businessType: item.businessType,
            platform: item.platform,
            url: item.url,
            videoLink: item.videoLink,
            description: item.description,
            image: null, // Keep null to not overwrite unless new image selected
            show: item.show
        });
        setEditingId(item._id);
        setImagePreview(item.image ? `${API_URL}/uploads/${item.image}` : null);
        setShowModal(true);
    };

    // ============== Delete Portfolio ==============
    const handleDelete = async (id) => {
        if (!window.confirm("Are you sure you want to delete this portfolio item?")) return;

        setLoading(true);
        try {
            const res = await axios.delete(`${API_URL}/admin/portfolio/delete/${id}`, {
                withCredentials: true
            });
            if (res.data.success) {
                // alert("Details deleted successfully");
                fetchPortfolios();
            }
        } catch (error) {
            console.error("Error deleting portfolio:", error);
            alert("Failed to delete portfolio");
        } finally {
            setLoading(false);
        }
    };

    // ============== Toggle Status ==============
    const handleToggleStatus = async (item) => {
        try {
            const res = await axios.patch(`${API_URL}/admin/portfolio/toggle/${item._id}`, {}, {
                withCredentials: true
            });
            if (res.data.success) {
                fetchPortfolios();
            }
        } catch (error) {
            console.error("Error toggling status:", error);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <MdIcons.MdDashboard className="text-primary" /> Manage Portfolio
                    </h2>
                    <p className="text-sm text-gray-500 mt-1">Showcase your projects and success stories</p>
                </div>
                <button
                    className="flex items-center justify-center px-4 py-2.5 bg-primary text-white rounded-lg hover:shadow-lg hover:bg-primary-dark transition-all duration-200 font-medium"
                    onClick={() => { resetForm(); setShowModal(true); }}
                >
                    <AiIcons.AiOutlinePlus className="mr-2" />
                    Add Portfolio
                </button>
            </div>

            {/* Content Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Project</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Platform</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading && portfolios.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        <div className="flex justify-center items-center">
                                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                                        </div>
                                    </td>
                                </tr>
                            ) : portfolios.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                                        No portfolio items found.
                                    </td>
                                </tr>
                            ) : (
                                portfolios.map((item) => (
                                    <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gray-100 overflow-hidden border border-gray-200">
                                                    {item.image ? (
                                                        <img
                                                            src={`${API_URL}/uploads/${item.image}`}
                                                            alt=""
                                                            className="w-full h-full object-cover"
                                                        />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                            <FaIcons.FaImage />
                                                        </div>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="font-medium text-gray-900">{item.name}</div>
                                                    <div className="text-xs text-gray-500 truncate max-w-xs">{item.url}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                                {item.category}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {item.platform}
                                        </td>
                                        <td className="px-6 py-4">
                                            <button
                                                onClick={() => handleToggleStatus(item)}
                                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${item.show ? 'bg-green-500' : 'bg-gray-200'}`}
                                            >
                                                <span
                                                    aria-hidden="true"
                                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${item.show ? 'translate-x-5' : 'translate-x-0'}`}
                                                />
                                            </button>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                                    onClick={() => setViewData(item)}
                                                    title="View"
                                                >
                                                    <MdIcons.MdRemoveRedEye />
                                                </button>
                                                <button
                                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                                    onClick={() => handleEdit(item)}
                                                    title="Edit"
                                                >
                                                    <FaIcons.FaEdit />
                                                </button>
                                                <button
                                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                                    onClick={() => handleDelete(item._id)}
                                                    title="Delete"
                                                >
                                                    <MdIcons.MdOutlineDeleteForever />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto scrollbar-hide">
                        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 sticky top-0 z-10">
                            <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                                {editingId ? <><FaIcons.FaEdit className="text-primary" /> Edit Portfolio</> : <><AiIcons.AiOutlinePlus className="text-primary" /> Add New Portfolio</>}
                            </h3>
                            <button
                                onClick={resetForm}
                                className="text-gray-400 hover:text-gray-600 transition-colors p-1"
                            >
                                <MdIcons.MdClose className="text-xl" />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="p-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Project Name *</label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="e.g., Infinex Website"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Category *</label>
                                    <select
                                        name="category"
                                        value={formData.category}
                                        onChange={handleInputChange}
                                        required
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all bg-white"
                                    >
                                        <option value="" disabled>Select Category</option>
                                        <option value="Web Development">Web Development</option>
                                        <option value="App Development">App Development</option>
                                        <option value="UI/UX Design">UI/UX Design</option>
                                        <option value="Digital Marketing">Digital Marketing</option>
                                        <option value="SEO">SEO</option>
                                        <option value="Other">Other</option>
                                    </select>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Business Type</label>
                                    <input
                                        type="text"
                                        name="businessType"
                                        value={formData.businessType}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="e.g., E-commerce"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Platform</label>
                                    <input
                                        type="text"
                                        name="platform"
                                        value={formData.platform}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="e.g., React, Shopify"
                                    />
                                </div>
                                <div className="flex items-center pt-6">
                                    <label className="inline-flex items-center cursor-pointer">
                                        <div className="relative">
                                            <input
                                                type="checkbox"
                                                name="show"
                                                checked={formData.show}
                                                onChange={handleInputChange}
                                                className="sr-only"
                                            />
                                            <div className={`block w-10 h-6 rounded-full transition-colors ${formData.show ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                            <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.show ? 'transform translate-x-4' : ''}`}></div>
                                        </div>
                                        <span className="ml-3 text-sm font-medium text-gray-700">Display Publicly</span>
                                    </label>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Project URL</label>
                                    <input
                                        type="text"
                                        name="url"
                                        value={formData.url}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-semibold text-gray-700 mb-1">Video Link</label>
                                    <input
                                        type="text"
                                        name="videoLink"
                                        value={formData.videoLink}
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                        placeholder="YouTube/Vimeo link"
                                    />
                                </div>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
                                <textarea
                                    name="description"
                                    value={formData.description}
                                    onChange={handleInputChange}
                                    rows="3"
                                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-all"
                                    placeholder="Brief project description..."
                                />
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Project Image</label>
                                <div className="flex items-start gap-4">
                                    <div className="flex-1">
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className="block w-full text-sm text-gray-500
                                                file:mr-4 file:py-2 file:px-4
                                                file:rounded-full file:border-0
                                                file:text-sm file:font-semibold
                                                file:bg-blue-50 file:text-blue-700
                                                hover:file:bg-blue-100
                                            "
                                        />
                                        <p className="text-xs text-gray-500 mt-1">Recommended size: 800x600px. Max: 5MB.</p>
                                    </div>
                                    {imagePreview && (
                                        <div className="w-24 h-24 rounded-lg border border-gray-200 overflow-hidden bg-gray-50">
                                            <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex gap-3 pt-4 border-t border-gray-100">
                                <button
                                    type="button"
                                    onClick={resetForm}
                                    className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="flex-1 px-6 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark font-medium shadow-lg hover:shadow-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Saving...' : (editingId ? 'Update Portfolio' : 'Add Portfolio')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* View Details Modal */}
            {viewData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-hide relative">
                        <button
                            onClick={() => setViewData(null)}
                            className="absolute top-4 right-4 bg-white/80 hover:bg-white p-2 rounded-full text-gray-600 z-10 shadow-sm transition-all"
                        >
                            <MdIcons.MdClose className="text-xl" />
                        </button>

                        <div className="w-full h-56 bg-gray-100 relative">
                            {viewData.image ? (
                                <img
                                    src={`${API_URL}/uploads/${viewData.image}`}
                                    alt={viewData.name}
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                    <FaIcons.FaImage className="text-4xl" />
                                </div>
                            )}
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-6 pt-12">
                                <h3 className="text-2xl font-bold text-white">{viewData.name}</h3>
                                <div className="text-white/80 text-sm">{viewData.category}</div>
                            </div>
                        </div>

                        <div className="p-6 space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Business Type</h4>
                                    <p className="font-medium text-gray-900">{viewData.businessType || 'N/A'}</p>
                                </div>
                                <div>
                                    <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">Platform</h4>
                                    <p className="font-medium text-gray-900">{viewData.platform || 'N/A'}</p>
                                </div>
                            </div>

                            <div>
                                <h4 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-1">Description</h4>
                                <p className="text-gray-700 leading-relaxed">{viewData.description || 'No description provided.'}</p>
                            </div>

                            <div className="pt-4 border-t border-gray-100 flex flex-col gap-2">
                                {viewData.url && (
                                    <a href={formatUrl(viewData.url)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-primary hover:text-primary-dark font-medium transition-colors">
                                        <FaIcons.FaLink /> Project Link
                                    </a>
                                )}
                                {viewData.videoLink && (
                                    <a href={formatUrl(viewData.videoLink)} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-red-600 hover:text-red-700 font-medium transition-colors">
                                        <FaIcons.FaYoutube /> Video Walkthrough
                                    </a>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManagePortfolio;
